import React, { useEffect, useState } from 'react';
import { be1Fetch } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';

interface Props {
  refs: string[];                      // 캔버스에서 참조하는 ${이름} 목록
  onRun: (conf: Record<string, string>) => void;
  onClose: () => void;
}

/**
 * 실행 파라미터 입력 — 캔버스가 참조하는 ${변수} 를 나열하고 값을 넣어 실행 conf 로 넘긴다.
 * 비워 두면 노드 파라미터/전역변수 값이 그대로 쓰인다(conf 가 최우선).
 */
const RunParamsDialog: React.FC<Props> = ({ refs, onRun, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [globals, setGlobals] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [extra, setExtra] = useState<Array<{ name: string; value: string }>>([]);
  useEffect(() => {
    be1Fetch('/api/variables').then((r) => r.json()).then((j) => {
      const m: Record<string, string> = {}; (j.items || []).forEach((i: any) => { m[i.name] = i.value ?? ''; }); setGlobals(m);
    }).catch(() => {});
  }, []);
  const run = () => {
    const conf: Record<string, string> = {};
    refs.forEach((k) => { if (values[k] !== undefined && values[k] !== '') conf[k] = values[k]; });
    extra.forEach((e) => { if (e.name.trim()) conf[e.name.trim()] = e.value; });
    onRun(conf);
  };
  const inp = 'border border-gray-300 rounded px-2 py-1 text-sm bg-white font-mono';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[560px] max-w-[95vw] max-h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('dialog:params.title')}</div>
            <div className="text-xs text-gray-500">{t('dialog:params.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        <div className="p-4 overflow-auto">
          {refs.length === 0 && extra.length === 0 && <div className="text-xs text-gray-500 mb-2">{t('dialog:params.noRefs1')}<code>{t('dialog:params.varRef')}</code>{t('dialog:params.noRefs2')}</div>}
          {refs.length > 0 && (
            <table className="w-full text-xs mb-2">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-1 w-[36%]">{t('dialog:params.variable')}</th><th className="py-1">{t('dialog:params.valueDefault')}</th></tr></thead>
              <tbody>
                {refs.map((k) => (
                  <tr key={k} className="border-b border-gray-100">
                    <td className="py-1 font-mono text-gray-800">${'{'}{k}{'}'}</td>
                    <td className="py-1"><input className={`${inp} w-full`} value={values[k] ?? ''} placeholder={globals[k] !== undefined ? t('dialog:params.globalValue', { value: globals[k] }) : t('dialog:params.noGlobal')} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {extra.map((e, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <input className={`${inp} w-[36%]`} value={e.name} placeholder="NAME" onChange={(ev) => setExtra((x) => x.map((r, k) => (k === i ? { ...r, name: ev.target.value } : r)))} />
              <input className={`${inp} flex-1`} value={e.value} placeholder={t('dialog:params.value')} onChange={(ev) => setExtra((x) => x.map((r, k) => (k === i ? { ...r, value: ev.target.value } : r)))} />
              <button className="text-gray-500 hover:text-red-500" onClick={() => setExtra((x) => x.filter((_, k) => k !== i))}>✕</button>
            </div>
          ))}
          <button className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => setExtra((x) => [...x, { name: '', value: '' }])}>{t('dialog:params.add')}</button>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white" onClick={run}>{t('common:ui.run')}</button>
        </div>
      </div>
    </div>
  );
};

export default RunParamsDialog;
